import type { LlmProfileRow } from '../llmProfileRows';
import type { ModelPriceView } from '../types';
import { RecordPriceForm } from './RecordPriceForm';

/**
 * What an expanded row's single cell holds (spec 136 US4, US6).
 *
 * Its own file so the table stays about markup and this stays about content — the two
 * change for different reasons, and ten columns of markup is already as much as one
 * file should carry.
 *
 * **Expanding makes no request** (FR-017, SC-004). Every name below is already in the
 * one listing response the section fetched, so expanding is a pure disclosure: no
 * spinner, no second read per row, and no N+1 on a registry of a hundred profiles.
 */
export function LlmProfileRowDetail({
  row,
  /**
   * This row's model's current price, if any — passed through to the form so "Change
   * rate" starts from what is in effect rather than a blank sheet. `undefined` for a
   * model with no rate yet, or while the lookup is unresolved.
   */
  price,
  /** Whether **this row's model** is the one with its price form open (FR-016). */
  pricing,
  onPricingDone,
}: {
  row: LlmProfileRow;
  price: ModelPriceView | undefined;
  pricing: boolean;
  onPricingDone: () => void;
}) {
  const { workspaces, workflows } = row.usage;

  return (
    <div className="flex flex-col gap-4">
      {/*
        The form first: it is the thing the reader just asked for by pressing a rate
        action, and the usage lists are what the row discloses by default.
      */}
      {pricing ? (
        <RecordPriceForm
          modelKey={row.model}
          currentPrice={price}
          onDone={onPricingDone}
        />
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2">
        <section className="flex flex-col gap-1.5">
          <h4 className="text-xs font-medium">Workspaces using this profile</h4>
          {/*
            An explicit sentence, never a blank list (FR-007): "no workspace uses
            this" and "we could not find out" must not render identically, and an
            empty `<ul>` is indistinguishable from a cell that failed to fill.
          */}
          {workspaces.length === 0 ? (
            <p className="text-muted-foreground text-xs">
              No workspace uses this profile.
            </p>
          ) : (
            <ul className="text-muted-foreground flex flex-col gap-1 text-xs">
              {workspaces.map((workspace) => (
                <li key={workspace.id} className="truncate">
                  {workspace.name}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="flex flex-col gap-1.5">
          <h4 className="text-xs font-medium">Workflows using this profile</h4>
          {workflows.length === 0 ? (
            <p className="text-muted-foreground text-xs">
              No workflow uses this profile.
            </p>
          ) : (
            <ul className="text-muted-foreground flex flex-col gap-1 text-xs">
              {workflows.map((workflow) => (
                <li key={workflow.id} className="truncate">
                  {/*
                    The workspace is named beside the workflow because workflow names
                    repeat across workspaces — "Nightly build" alone does not tell an
                    admin which board they would be changing.
                  */}
                  {workflow.name} · {workflow.workspace_name}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
